<template>
  <form-wrapper :validator="$v.cashMovement">
    <v-toolbar
      v-if="isInitial"
      color="white"
    >
      <v-toolbar-title>
        <v-icon class="mr-2">fal fa-money-check-alt</v-icon>
        {{ $t('Opening Float') }}
      </v-toolbar-title>
      <v-spacer />
      <template v-if="expectedInitial >= 0">
        <v-chip
          class="text-center"
          label
          outlined
        >
          <span class="pt-1">{{ `${$t('Expected')}` }}: {{ expectedInitial | money }}</span>
        </v-chip>
      </template>
    </v-toolbar>
    <v-card tile>
      <v-card-text>
        <v-radio-group
          v-if="!isInitial"
          v-model="cashMovement.type"
          :value="CASH_MOVEMENT_ADD"
          class="mt-2 mb-4"
          hide-details
          row
        >
          <v-radio
            ref="radioAdd"
            :label="$t('Cash-In')"
            :value="CASH_MOVEMENT_ADD"
            color="grey darken-2"
          />
          <v-radio
            ref="radioRemove"
            :label="$t('Cash-Out')"
            :value="CASH_MOVEMENT_REMOVE"
            color="grey darken-2"
          />
        </v-radio-group>
        <p
          v-show="isCashIn && !isInitial"
          class="font-italic"
        >
          {{ $t('This allows you to track any additional money you put into your cash drawer. This is used to increase your float amount during the day if required.') }}
        </p>
        <p
          v-show="isCashOut"
          class="font-italic"
        >
          {{ $t('You can use this option to track any cash you remove from your cash drawer. Cash out can be used to track actions such as safe drops, where you may need to remove cash from your drawer during the day for safekeeping.') }}
        </p>
        <form-group name="amount">
          <template v-slot:default="{attrs}">
            <numeric-keyboard
              ref="keyboard"
              :clear-input="clearInput"
              class="mb-4"
              max-width="520px"
              wrapper-background="transparent"
              @clear="$set(cashMovement, 'amount', null)"
              @input="$set(cashMovement, 'amount', parseFloat($event))"
            >
              <v-text-field
                v-comma
                slot="input"
                slot-scope="{ setKeyboardInput }"
                ref="input"
                v-bind="attrs"
                v-model.number="cashMovement.amount"
                :label="$t('Amount')"
                filled
                hide-details
                @input="setKeyboardInput($event)"
                @keypress.enter="submit"
              >
                <template
                  v-slot:append
                  v-if="isInitial"
                >
                  <v-dialog
                    v-model="showDetailedCountDialog"
                    eager
                    max-width="550px"
                  >
                    <forms-cash-registers-detailed-count
                      currency="EUR"
                      @saved="processDetailedCashCount"
                    />
                  </v-dialog>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        class="mt-n1"
                        v-on="on"
                        @click="showDetailedCountDialog = true"
                      >
                        <v-icon>fal fa-calculator</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('Enter a {0}', [ $t('Detailed cash count').toLowerCase()]) }}</span>
                  </v-tooltip>
                </template>
              </v-text-field>
            </numeric-keyboard>
          </template>
        </form-group>
        <form-group name="note">
          <template v-slot:default="{attrs}">
            <v-textarea
              v-bind="attrs"
              v-model="cashMovement.note"
              :hint="$t('Max. Characters: {0}', ['255'])"
              :label="$t('Note')"
              counter="255"
              filled
              rows="4"
            />
          </template>
        </form-group>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn
          :loading="$wait.is('saving cashmovement')"
          class="font-weight-bold"
          color="primary"
          text
          @click="submit"
        >
          <template v-if="isInitial">
            {{ $t('Open Cash Register') }}
          </template>
          <template v-else>
            {{ $t('Save') }}
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </form-wrapper>
</template>

<script>
import * as Cookies from 'js-cookie'
import moment from 'moment'
import validationMixin from '@/utils/mixins/validationMixin'
import focusInputMixin from '@/utils/mixins/focusInputMixin'
import { required, decimal, minValue } from 'vuelidate/lib/validators'
import { cookieDomain } from '@/utils/variables'
import NumericKeyboard from '@/components/NumericKeyboard'
import FormsCashRegistersDetailedCount from '@/components/forms/cash-registers/FormsCashRegistersDetailedCount'
import { CASH_MOVEMENT_ADD, CASH_MOVEMENT_REMOVE, CASH_MOVEMENT_INITIAL } from '@/utils/types/cash-movement'

export default {
  name: 'FormsCashMovementsSave',

  components: {
    NumericKeyboard,
    FormsCashRegistersDetailedCount
  },

  mixins: [ validationMixin, focusInputMixin ],

  props: {
    movementType: {
      type: String,
      default: CASH_MOVEMENT_ADD
    },
    clearInput: {
      type: Boolean,
      default: false
    },
    expectedInitial: {
      type: Number,
      required: false,
      default: null
    }
  },

  data () {
    return {
      formTitle: this.$t('Add {0}', [this.$t('Cash')]),
      cashMovement: {},
      showDetailedCountDialog: false,
      CASH_MOVEMENT_ADD,
      CASH_MOVEMENT_REMOVE,
      CASH_MOVEMENT_INITIAL
    }
  },

  computed: {
    isCashIn () {
      return this.cashMovement.type === CASH_MOVEMENT_ADD
    },
    isCashOut () {
      return this.cashMovement.type === CASH_MOVEMENT_REMOVE
    },
    isInitial () {
      return this.cashMovement.type === CASH_MOVEMENT_INITIAL
    }
  },

  validations () {
    const validations = {
      cashMovement: {
        amount: {
          required,
          decimal,
          minValue: minValue(0)
        }
      }
    }

    return validations
  },

  watch: {
    movementType: {
      immediate: true,
      handler (movementType) {
        if (movementType) {
          this.$set(this.cashMovement, 'type', movementType)
        }
      }
    },
    clearInput: {
      immediate: true,
      handler (val) {
        if (val) {
          this.$set(this.cashMovement, 'amount', null)
        }
      }
    }
  },

  methods: {
    submit () {
      if (!this.validate()) return
      if (!this.isInitial) {
        return this.dispatchOpeningToStore()
      }

      // Only check matching amounts if we're opening the cash register
      // This shouldn't be done when we are adding/removing money from the drawer
      if (this.expectedInitial !== this.cashMovement.amount) {
        const cashDifferenceAmount = Math.round((this.cashMovement.amount - this.expectedInitial) * 100) / 100
        this.$swal({
          title: this.$t('Warning'),
          text: `${this.$t('The counted amount doesn\'t match the expected amount.')} ${this.$t('The difference is {0}.', [this.$options.filters.money(cashDifferenceAmount)])}`,
          type: 'warning',
          cancelButtonText: this.$t('Cancel'),
          confirmButtonText: this.$t('Continue'),
          showCancelButton: true,
          reverseButtons: true
        })
          .then((result) => {
            if (result.value) {
              this.dispatchOpeningToStore()
            }
          })
      } else {
        this.dispatchOpeningToStore()
      }
    },
    dispatchOpeningToStore () {
      if (this.$wait.is('saving cashmovement')) return
      this.$wait.start('saving cashmovement')
      return this.$store.dispatch('SAVE_CASH_MOVEMENT', {
        cashRegisterIdentifier: this.$store.state.identifier,
        cashMovement: this.cashMovement
      }).then((data) => {
        this.$emit('saved', data.movement)
        this.helpers.toastSuccess(data.message)

        // reset the form
        this.cashMovement = { type: CASH_MOVEMENT_ADD }
        // reset validation
        this.$v.cashMovement.$reset()

        // Do logic when cashregister is opened
        if (this.isInitial) this.afterCashRegisterOpened()
      })
        .catch(data => {
          if (data && data.message) this.helpers.toastError(data.message)
        })
        .finally(() => this.$wait.end('saving cashmovement'))
    },
    openDrawer () {
      this.$refs.drawer.openDrawer()
    },
    processDetailedCashCount (data) {
      this.$set(this.cashMovement, 'amount', data.countedTotal)
      this.$set(this.cashMovement, 'detailed_amount', data.countedValues)
      this.showDetailedCountDialog = false
    },
    afterCashRegisterOpened () {
      Cookies.set('cashRegisterOpened', JSON.stringify({
        posFullDomain: window.location.origin,
        posSubDomain: window.location.hostname.split('.')[0],
        timestamp: moment()
      }), {
        domain: cookieDomain,
        expires: new Date(moment().endOf('day'))
      })

      this.$router.push({ name: 'Cart' })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/forms/cash-movements/FormsCashMovementsSave.vue